import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Mail, MessageSquare, Phone, Plus, Search, SlidersHorizontal, User, X } from "lucide-react";
import * as React from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import { Avatar, AvatarFallback } from "@/components/avatar";
import { Badge } from "@/components/badge";
import { Button } from "@/components/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/dialog";
import { Input } from "@/components/input";
import { Label } from "@/components/label";
import { Layout } from "@/components/layout";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/select";
import { Textarea } from "@/components/textarea";
import {
  type Customer,
  type CustomerStatus,
  formatCurrency,
  formatDate,
  getInitials,
  getSourceDisplayName,
  type Interaction,
  type LeadSource,
} from "@/lib/types";

// Color palette for avatar backgrounds - Fresh design
const avatarColors = [
  "bg-indigo-100 text-indigo-700",
  "bg-violet-100 text-violet-700",
  "bg-emerald-100 text-emerald-700",
  "bg-amber-100 text-amber-700",
  "bg-rose-100 text-rose-700",
  "bg-cyan-100 text-cyan-700",
];

function getAvatarColor(name: string): string {
  const index =
    name.split("").reduce((acc, char) => acc + char.charCodeAt(0), 0) % avatarColors.length;
  return avatarColors[index];
}

const emptyCustomer = {
  name: "",
  email: "",
  phone: "",
  address: "",
  source: "walk-in" as LeadSource,
  status: "warm" as CustomerStatus,
  vehicle_interest: "",
  budget_min: "",
  budget_max: "",
  notes: "",
};

export function Customers() {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [search, setSearch] = React.useState("");
  const [statusFilter, setStatusFilter] = React.useState<string>("all");
  const [sourceFilter, setSourceFilter] = React.useState<string>("all");
  const [showFilters, setShowFilters] = React.useState(false);

  // Dialog states
  const [isAddDialogOpen, setIsAddDialogOpen] = React.useState(searchParams.get("new") === "true");
  const [isEditDialogOpen, setIsEditDialogOpen] = React.useState(false);
  const [isDetailOpen, setIsDetailOpen] = React.useState(false);
  const [selectedCustomer, setSelectedCustomer] = React.useState<Customer | null>(null);
  const [formData, setFormData] = React.useState(emptyCustomer);
  const [newNote, setNewNote] = React.useState("");

  // Fetch customers
  const { data: customers = [], isLoading } = useQuery<Customer[]>({
    queryKey: ["customers", statusFilter, sourceFilter],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (statusFilter !== "all") params.append("status", statusFilter);
      if (sourceFilter !== "all") params.append("source", sourceFilter);
      const res = await fetch(`/api/customers?${params}`);
      if (!res.ok) throw new Error("Failed to fetch customers");
      return res.json();
    },
  });

  // Fetch interactions for selected customer
  const { data: interactions = [] } = useQuery<Interaction[]>({
    queryKey: ["interactions", selectedCustomer?.id],
    queryFn: async () => {
      if (!selectedCustomer) return [];
      const res = await fetch(`/api/interactions?customer_id=${selectedCustomer.id}`);
      if (!res.ok) throw new Error("Failed to fetch interactions");
      return res.json();
    },
    enabled: !!selectedCustomer,
  });

  // Create customer mutation
  const createCustomer = useMutation({
    mutationFn: async (data: typeof formData) => {
      const res = await fetch("/api/customers", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...data,
          budget_min: data.budget_min ? parseInt(data.budget_min) : null,
          budget_max: data.budget_max ? parseInt(data.budget_max) : null,
        }),
      });
      if (!res.ok) throw new Error("Failed to create customer");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["customers"] });
      queryClient.invalidateQueries({ queryKey: ["dashboard"] });
      setIsAddDialogOpen(false);
      setFormData(emptyCustomer);
      setSearchParams({});
    },
  });

  // Update customer mutation
  const updateCustomer = useMutation({
    mutationFn: async (data: typeof formData & { id: number }) => {
      const res = await fetch(`/api/customers/${data.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...data,
          budget_min: data.budget_min ? parseInt(data.budget_min as string) : null,
          budget_max: data.budget_max ? parseInt(data.budget_max as string) : null,
        }),
      });
      if (!res.ok) throw new Error("Failed to update customer");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["customers"] });
      queryClient.invalidateQueries({ queryKey: ["dashboard"] });
      setIsEditDialogOpen(false);
    },
  });

  // Add note mutation
  const addNote = useMutation({
    mutationFn: async ({ customerId, content }: { customerId: number; content: string }) => {
      const res = await fetch("/api/interactions", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          customer_id: customerId,
          type: "note",
          content,
        }),
      });
      if (!res.ok) throw new Error("Failed to add note");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["interactions"] });
      setNewNote("");
    },
  });

  // Filter customers by search
  const filteredCustomers = customers.filter(
    (customer) =>
      customer.name.toLowerCase().includes(search.toLowerCase()) ||
      customer.email?.toLowerCase().includes(search.toLowerCase()) ||
      customer.phone?.includes(search)
  );

  // Handle customer selection from URL
  React.useEffect(() => {
    if (id) {
      const customer = customers.find((c) => c.id === parseInt(id));
      if (customer) {
        setSelectedCustomer(customer);
        setIsDetailOpen(true);
      }
    }
  }, [id, customers]);

  const handleOpenCustomer = (customer: Customer) => {
    setSelectedCustomer(customer);
    setIsDetailOpen(true);
    navigate(`/customers/${customer.id}`);
  };

  const handleCloseDetail = () => {
    setIsDetailOpen(false);
    setSelectedCustomer(null);
    navigate("/customers");
  };

  const handleEdit = (customer: Customer) => {
    setFormData({
      name: customer.name,
      email: customer.email || "",
      phone: customer.phone || "",
      address: customer.address || "",
      source: customer.source,
      status: customer.status,
      vehicle_interest: customer.vehicle_interest || "",
      budget_min: customer.budget_min?.toString() || "",
      budget_max: customer.budget_max?.toString() || "",
      notes: customer.notes || "",
    });
    setIsEditDialogOpen(true);
  };

  return (
    <Layout>
      <div className="space-y-6">
        {/* Page Header */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 sm:text-3xl">ลูกค้า</h1>
            <p className="mt-1 text-slate-500">จัดการลูกค้าและความสัมพันธ์</p>
          </div>
          <Button
            className="rounded-xl bg-indigo-500 hover:bg-indigo-600"
            onClick={() => setIsAddDialogOpen(true)}
          >
            <Plus className="mr-2 size-4" />
            เพิ่มลูกค้า
          </Button>
        </div>

        {/* Search and Filters */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-slate-400" />
            <Input
              placeholder="ค้นหาด้วยชื่อ, อีเมล, หรือเบอร์โทร..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="rounded-xl border-slate-200 pl-9"
            />
          </div>
          <Button
            variant="outline"
            className="rounded-xl border-slate-200"
            onClick={() => setShowFilters(!showFilters)}
          >
            <SlidersHorizontal className="mr-2 size-4" />
            ตัวกรอง
          </Button>
        </div>

        {/* Filters */}
        {showFilters && (
          <Card className="rounded-2xl border-slate-200">
            <CardContent className="flex flex-wrap gap-4 p-4">
              <div className="min-w-[150px]">
                <Label className="mb-2 block text-xs text-slate-500">สถานะ</Label>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="rounded-xl border-slate-200">
                    <SelectValue placeholder="ทุกสถานะ" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">ทุกสถานะ</SelectItem>
                    <SelectItem value="hot">Hot</SelectItem>
                    <SelectItem value="warm">Warm</SelectItem>
                    <SelectItem value="cold">Cold</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="min-w-[150px]">
                <Label className="mb-2 block text-xs text-slate-500">แหล่งที่มา</Label>
                <Select value={sourceFilter} onValueChange={setSourceFilter}>
                  <SelectTrigger className="rounded-xl border-slate-200">
                    <SelectValue placeholder="ทุกแหล่ง" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">ทุกแหล่ง</SelectItem>
                    <SelectItem value="walk-in">เข้ามาเอง</SelectItem>
                    <SelectItem value="phone">โทรศัพท์</SelectItem>
                    <SelectItem value="website">เว็บไซต์</SelectItem>
                    <SelectItem value="social">โซเชียลมีเดีย</SelectItem>
                    <SelectItem value="referral">แนะนำ</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Customers List */}
        <Card className="rounded-2xl border-slate-200 shadow-sm">
          <CardContent className="p-0">
            {isLoading ? (
              <div className="space-y-3 p-4">
                {[1, 2, 3, 4, 5].map((i) => (
                  <div
                    key={i}
                    className="flex animate-pulse items-center gap-4 rounded-xl bg-slate-50 p-4"
                  >
                    <div className="size-12 rounded-full bg-slate-100" />
                    <div className="flex-1 space-y-2">
                      <div className="h-4 w-32 rounded bg-slate-100" />
                      <div className="h-3 w-48 rounded bg-slate-100" />
                    </div>
                  </div>
                ))}
              </div>
            ) : filteredCustomers.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <div className="flex size-14 items-center justify-center rounded-2xl bg-slate-100">
                  <User className="size-7 text-slate-400" />
                </div>
                <p className="mt-3 text-sm font-medium text-slate-600">ไม่พบลูกค้า</p>
                <p className="text-xs text-slate-400">
                  {search ? "ลองปรับการค้นหาใหม่" : "เพิ่มลูกค้าคนแรกเพื่อเริ่มต้น"}
                </p>
                {!search && (
                  <Button
                    className="mt-4 rounded-xl bg-indigo-500 hover:bg-indigo-600"
                    size="sm"
                    onClick={() => setIsAddDialogOpen(true)}
                  >
                    <Plus className="mr-2 size-4" />
                    เพิ่มลูกค้า
                  </Button>
                )}
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {filteredCustomers.map((customer) => (
                  <button
                    key={customer.id}
                    onClick={() => handleOpenCustomer(customer)}
                    className="flex w-full items-center justify-between p-4 text-left transition-all hover:bg-slate-50"
                  >
                    <div className="flex items-center gap-4">
                      <Avatar className="size-12">
                        <AvatarFallback className={getAvatarColor(customer.name)}>
                          {getInitials(customer.name)}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="font-medium text-slate-900">{customer.name}</p>
                        <p className="text-sm text-slate-500">
                          {customer.vehicle_interest || "สอบถามทั่วไป"} •{" "}
                          {getSourceDisplayName(customer.source)}
                        </p>
                        <div className="mt-1 flex items-center gap-3 text-xs text-slate-400">
                          {customer.phone && (
                            <span className="flex items-center gap-1">
                              <Phone className="size-3" />
                              {customer.phone}
                            </span>
                          )}
                          {customer.email && (
                            <span className="flex items-center gap-1">
                              <Mail className="size-3" />
                              {customer.email}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      {customer.budget_max && (
                        <span className="hidden text-sm font-medium text-slate-400 sm:block">
                          งบ: {formatCurrency(customer.budget_max)}
                        </span>
                      )}
                      <Badge variant={customer.status}>
                        {customer.status === "hot"
                          ? "Hot"
                          : customer.status === "warm"
                            ? "Warm"
                            : "Cold"}
                      </Badge>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Add Customer Dialog */}
      <Dialog
        open={isAddDialogOpen}
        onOpenChange={(open) => {
          setIsAddDialogOpen(open);
          if (!open) setSearchParams({});
        }}
      >
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Add New Customer</DialogTitle>
            <DialogDescription>Add a new lead or customer to your CRM</DialogDescription>
          </DialogHeader>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              createCustomer.mutate(formData);
            }}
            className="space-y-4"
          >
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <Label htmlFor="name">Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="phone">Phone</Label>
                <Input
                  id="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="source">Lead Source</Label>
                <Select
                  value={formData.source}
                  onValueChange={(value: LeadSource) => setFormData({ ...formData, source: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="walk-in">Walk-in</SelectItem>
                    <SelectItem value="phone">Phone Call</SelectItem>
                    <SelectItem value="website">Website</SelectItem>
                    <SelectItem value="social">Social Media</SelectItem>
                    <SelectItem value="referral">Referral</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="status">Status</Label>
                <Select
                  value={formData.status}
                  onValueChange={(value: CustomerStatus) =>
                    setFormData({ ...formData, status: value })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="hot">Hot</SelectItem>
                    <SelectItem value="warm">Warm</SelectItem>
                    <SelectItem value="cold">Cold</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="sm:col-span-2">
                <Label htmlFor="vehicle_interest">Vehicle Interest</Label>
                <Input
                  id="vehicle_interest"
                  placeholder="e.g., SUV, Sedan, Honda Accord"
                  value={formData.vehicle_interest}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      vehicle_interest: e.target.value,
                    })
                  }
                />
              </div>
              <div>
                <Label htmlFor="budget_min">Budget Min</Label>
                <Input
                  id="budget_min"
                  type="number"
                  placeholder="10000"
                  value={formData.budget_min}
                  onChange={(e) => setFormData({ ...formData, budget_min: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="budget_max">Budget Max</Label>
                <Input
                  id="budget_max"
                  type="number"
                  placeholder="25000"
                  value={formData.budget_max}
                  onChange={(e) => setFormData({ ...formData, budget_max: e.target.value })}
                />
              </div>
              <div className="sm:col-span-2">
                <Label htmlFor="address">Address</Label>
                <Input
                  id="address"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                />
              </div>
              <div className="sm:col-span-2">
                <Label htmlFor="notes">Notes</Label>
                <Textarea
                  id="notes"
                  placeholder="Any additional notes about this customer..."
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setIsAddDialogOpen(false);
                  setSearchParams({});
                }}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={createCustomer.isPending}>
                {createCustomer.isPending ? "Adding..." : "Add Customer"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Customer Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit Customer</DialogTitle>
            <DialogDescription>Update customer information</DialogDescription>
          </DialogHeader>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (selectedCustomer) {
                updateCustomer.mutate({
                  ...formData,
                  id: selectedCustomer.id,
                });
              }
            }}
            className="space-y-4"
          >
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <Label htmlFor="edit-name">Name *</Label>
                <Input
                  id="edit-name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="edit-phone">Phone</Label>
                <Input
                  id="edit-phone"
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="edit-email">Email</Label>
                <Input
                  id="edit-email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="edit-source">Lead Source</Label>
                <Select
                  value={formData.source}
                  onValueChange={(value: LeadSource) => setFormData({ ...formData, source: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="walk-in">Walk-in</SelectItem>
                    <SelectItem value="phone">Phone Call</SelectItem>
                    <SelectItem value="website">Website</SelectItem>
                    <SelectItem value="social">Social Media</SelectItem>
                    <SelectItem value="referral">Referral</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="edit-status">Status</Label>
                <Select
                  value={formData.status}
                  onValueChange={(value: CustomerStatus) =>
                    setFormData({ ...formData, status: value })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="hot">Hot</SelectItem>
                    <SelectItem value="warm">Warm</SelectItem>
                    <SelectItem value="cold">Cold</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="sm:col-span-2">
                <Label htmlFor="edit-vehicle_interest">Vehicle Interest</Label>
                <Input
                  id="edit-vehicle_interest"
                  placeholder="e.g., SUV, Sedan, Honda Accord"
                  value={formData.vehicle_interest}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      vehicle_interest: e.target.value,
                    })
                  }
                />
              </div>
              <div>
                <Label htmlFor="edit-budget_min">Budget Min</Label>
                <Input
                  id="edit-budget_min"
                  type="number"
                  value={formData.budget_min}
                  onChange={(e) => setFormData({ ...formData, budget_min: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="edit-budget_max">Budget Max</Label>
                <Input
                  id="edit-budget_max"
                  type="number"
                  value={formData.budget_max}
                  onChange={(e) => setFormData({ ...formData, budget_max: e.target.value })}
                />
              </div>
              <div className="sm:col-span-2">
                <Label htmlFor="edit-address">Address</Label>
                <Input
                  id="edit-address"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                />
              </div>
              <div className="sm:col-span-2">
                <Label htmlFor="edit-notes">Notes</Label>
                <Textarea
                  id="edit-notes"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsEditDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={updateCustomer.isPending}>
                {updateCustomer.isPending ? "Saving..." : "Save Changes"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Customer Detail Dialog */}
      <Dialog open={isDetailOpen} onOpenChange={handleCloseDetail}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
          {selectedCustomer && (
            <>
              <DialogHeader>
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-4">
                    <Avatar className="h-14 w-14">
                      <AvatarFallback className={getAvatarColor(selectedCustomer.name)}>
                        {getInitials(selectedCustomer.name)}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <DialogTitle className="text-xl">{selectedCustomer.name}</DialogTitle>
                      <DialogDescription>
                        Added {formatDate(selectedCustomer.created_at)}
                      </DialogDescription>
                    </div>
                  </div>
                  <Badge variant={selectedCustomer.status} className="mt-1">
                    {selectedCustomer.status.charAt(0).toUpperCase() +
                      selectedCustomer.status.slice(1)}
                  </Badge>
                </div>
              </DialogHeader>

              <div className="space-y-6">
                {/* Contact Info */}
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-medium">Contact Information</CardTitle>
                  </CardHeader>
                  <CardContent className="grid gap-3 text-sm sm:grid-cols-2">
                    {selectedCustomer.phone && (
                      <div className="flex items-center gap-2">
                        <Phone className="h-4 w-4 text-muted-foreground" />
                        <a
                          href={`tel:${selectedCustomer.phone}`}
                          className="text-blue-600 hover:underline"
                        >
                          {selectedCustomer.phone}
                        </a>
                      </div>
                    )}
                    {selectedCustomer.email && (
                      <div className="flex items-center gap-2">
                        <Mail className="h-4 w-4 text-muted-foreground" />
                        <a
                          href={`mailto:${selectedCustomer.email}`}
                          className="text-blue-600 hover:underline"
                        >
                          {selectedCustomer.email}
                        </a>
                      </div>
                    )}
                    <div>
                      <span className="text-muted-foreground">Source:</span>{" "}
                      {getSourceDisplayName(selectedCustomer.source)}
                    </div>
                    {selectedCustomer.vehicle_interest && (
                      <div>
                        <span className="text-muted-foreground">Interest:</span>{" "}
                        {selectedCustomer.vehicle_interest}
                      </div>
                    )}
                    {(selectedCustomer.budget_min || selectedCustomer.budget_max) && (
                      <div>
                        <span className="text-muted-foreground">Budget:</span>{" "}
                        {selectedCustomer.budget_min && formatCurrency(selectedCustomer.budget_min)}
                        {selectedCustomer.budget_min && selectedCustomer.budget_max && " - "}
                        {selectedCustomer.budget_max && formatCurrency(selectedCustomer.budget_max)}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Notes */}
                {selectedCustomer.notes && (
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm font-medium">Notes</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-muted-foreground">{selectedCustomer.notes}</p>
                    </CardContent>
                  </Card>
                )}

                {/* Interaction History */}
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-medium">Interaction History</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Add Note Form */}
                    <div className="flex gap-2">
                      <Input
                        placeholder="Add a note..."
                        value={newNote}
                        onChange={(e) => setNewNote(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" && newNote.trim()) {
                            addNote.mutate({
                              customerId: selectedCustomer.id,
                              content: newNote.trim(),
                            });
                          }
                        }}
                      />
                      <Button
                        size="icon"
                        disabled={!newNote.trim() || addNote.isPending}
                        onClick={() => {
                          if (newNote.trim()) {
                            addNote.mutate({
                              customerId: selectedCustomer.id,
                              content: newNote.trim(),
                            });
                          }
                        }}
                      >
                        <MessageSquare className="h-4 w-4" />
                      </Button>
                    </div>

                    {/* Interactions List */}
                    {interactions.length === 0 ? (
                      <p className="py-4 text-center text-sm text-muted-foreground">
                        No interactions yet
                      </p>
                    ) : (
                      <div className="space-y-3">
                        {interactions.map((interaction) => (
                          <div
                            key={interaction.id}
                            className="flex gap-3 rounded-lg bg-muted/50 p-3"
                          >
                            <MessageSquare className="mt-0.5 h-4 w-4 text-muted-foreground" />
                            <div className="flex-1">
                              <p className="text-sm">{interaction.content}</p>
                              <p className="mt-1 text-xs text-muted-foreground">
                                {formatDate(interaction.created_at)}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={handleCloseDetail}>
                  <X className="mr-2 h-4 w-4" />
                  Close
                </Button>
                <Button onClick={() => handleEdit(selectedCustomer)}>Edit Customer</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
